import React, { useEffect, useRef, useState } from "react";
import Index from "../../../Index";
import QRCode from "qrcode";
import { useReactToPrint } from "react-to-print";

const normalizeData = (fullRecordData) => {

  if (!fullRecordData) {
    return { printableItems: [], error: "No data provided" };
  }

  try {
    if (fullRecordData.id && fullRecordData.productCode && !fullRecordData.data) {

      const qty = parseInt(fullRecordData.qty);

      if (qty <= 0) {
        return {
          printableItems: [],
          productCode: fullRecordData.productCode,
          productName: fullRecordData.productName,
          brandName: fullRecordData.brandName,
          setId: fullRecordData.setId,
          productGroupname: fullRecordData.productGroupname,
        };
      }

      const singleItem = {
        colorId: fullRecordData.colorId,
        sizeId: fullRecordData.sizeId,
        sizeName: fullRecordData.sizeName,
        color_name: fullRecordData.color_name,
        size_name: fullRecordData.size_name,
        mrp: fullRecordData.mrp,
        generatedBarcode: fullRecordData.generatedBarcode,
        barcodeScan: fullRecordData.barcodeScan,
        createdAt: fullRecordData.createdAt,
        barcodeWidth: fullRecordData.barcodeWidth,
        barcodeHeight: fullRecordData.barcodeHeight,
        productCode: fullRecordData.productCode,
        productName: fullRecordData.productName,
        brandName: fullRecordData.brandName,
        productGroupname: fullRecordData.productGroupName,
        productUnitName: fullRecordData.productUnitName,
      };

      let itemsToPrint = [];
      for (let i = 0; i < qty; i++) {
        itemsToPrint.push(singleItem);
      }

      return {
        printableItems: itemsToPrint,
        productCode: fullRecordData.productCode,
        productName: fullRecordData.productName,
        brandName: fullRecordData.brandName,
        setId: fullRecordData.setId,
        productGroupname: fullRecordData.productGroupname,
      };
    }

    if (fullRecordData.data && typeof fullRecordData.data === "string") {

      const barcodeItems = JSON.parse(fullRecordData.data);

      if (!Array.isArray(barcodeItems) || barcodeItems.length === 0) {
        return { printableItems: [], error: "Parsed data is empty or not an array" };
      }

      let itemsToPrint = [];
      barcodeItems.forEach((item) => {
        const qty = parseInt(item.qty) || 0;

        if (qty > 0) {
          for (let i = 0; i < qty; i++) {
            itemsToPrint.push({
              ...item,
              productName: fullRecordData.productName || "",
              brandName: fullRecordData.brandName || "",
              productCode: fullRecordData.productCode || "",
              productGroupname: fullRecordData.productGroupname || "",
              barcodeScan: fullRecordData.barcodeScan,
              barcodeWidth: fullRecordData.barcodeWidth,
              barcodeHeight: fullRecordData.barcodeHeight,
              barcodeBrandImage: fullRecordData.barcodeBrandImage,
              barcodeAddress: fullRecordData.barcodeAddress,
              createdAt: item.createdAt || fullRecordData.createdAt,
            });
          }
        }
      });

      return {
        printableItems: itemsToPrint,
        productCode: fullRecordData.productCode,
        productName: fullRecordData.productName,
        brandName: fullRecordData.brandName,
        setId: fullRecordData.setId,
        productGroupname: fullRecordData.productGroupname,
      };
    }

    if (fullRecordData.colorId && fullRecordData.productCode) {

      const qty = parseInt(fullRecordData.qty) || 1;
      if (qty <= 0) {
        return {
          printableItems: [],
          productCode: fullRecordData.productCode,
          productName: fullRecordData.productName,
          brandName: fullRecordData.brandName,
          setId: fullRecordData.setId,
          productGroupname: fullRecordData.productGroupname,
        };
      }

      return {
        printableItems: [fullRecordData],
        productCode: fullRecordData.productCode,
        productName: fullRecordData.productName,
        brandName: fullRecordData.brandName,
        setId: fullRecordData.setId,
        productGroupname: fullRecordData.productGroupname,
      };
    }

    return { printableItems: [], error: "Unknown data format" };
  } catch (error) {
    return { printableItems: [], error: error.message };
  }
};

const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";

export default function QrBannerPage(props) {
  const { setOpen, fullRecordData } = props;

  // Company-level barcode settings (from companyMaster via generateQRCode endpoint)
  const companyBrandName = fullRecordData?.barcodeBrandName || "";
  const companyBrandImage = fullRecordData?.barcodeBrandImage || null;
  const companyAddress = fullRecordData?.barcodeAddress || "";
  const fontSize = fullRecordData?.fontSize ? parseFloat(fullRecordData.fontSize) : null;
  // label-key width: default 55px, +10px for every 1px fontSize increase above 9
  const printDesignNo = fullRecordData?.printDesignNo == null ? true : toBool(fullRecordData?.printDesignNo);
  const printColour = fullRecordData?.printColour == null ? true : toBool(fullRecordData?.printColour);
  const printSizeCM = fullRecordData?.printSizeCM == null ? true : toBool(fullRecordData?.printSizeCM);
  const printStyle = fullRecordData?.printStyle == null ? true : toBool(fullRecordData?.printStyle);
  const printMFGDate = fullRecordData?.printMFGDate == null ? true : toBool(fullRecordData?.printMFGDate);
  const printMRP = fullRecordData?.printMRP == null ? true : toBool(fullRecordData?.printMRP);
  const qrSizeMM = fullRecordData?.qrSize ? parseFloat(fullRecordData.qrSize) : 10;
  const barcodeQty = fullRecordData?.printQty || "";

  const [printableItems, setPrintableItems] = useState([]);
  const [gridColumns, setGridColumns] = useState(1);
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [recordMetadata, setRecordMetadata] = useState({});

  const printRef = useRef();
  const containerRef = useRef();

  useEffect(() => {
    if (fullRecordData) {
      setLoading(true);
      setDataError(null);

      try {
        const { printableItems: items, error, ...metadata } = normalizeData(fullRecordData);

        if (error) {
          console.warn("Data normalization warning:", error);
          setDataError(error);
          setPrintableItems([]);
          setLoading(false);
          return;
        }

        if (!items || items.length === 0) {
          setDataError("No barcode items found");
          setPrintableItems([]);
          setLoading(false);
          return;
        }

        setRecordMetadata(metadata);

        const generateQRCodes = async () => {
          try {
            const updatedItems = await Promise.all(
              items.map(async (item) => {
                let qrUrl = null;

                if (item.barcodeScan) {
                  try {
                    qrUrl = await QRCode.toDataURL(
                      `${item.productCode},${item.colorId},${metadata.setId || item.setId}`,
                      {
                        width: 200,
                        margin: 1,
                        errorCorrectionLevel: 'M'
                      }
                    );
                  } catch (qrError) {
                    console.error(`QR Generation Error for item:`, qrError);
                  }
                }

                return { ...item, qrUrl };
              })
            );
            setPrintableItems(updatedItems);
          } catch (qrError) {
            setDataError("Failed to generate QR codes");
            setPrintableItems([]);
          } finally {
            setLoading(false);
          }
        };

        generateQRCodes();
      } catch (error) {
        setDataError(error.message);
        setLoading(false);
      }
    } else {
      console.warn("fullRecordData is missing");
      setDataError("No record data provided");
      setLoading(false);
    }
  }, [fullRecordData]);

  // Calculate grid columns based on container width and physical tag width
  useEffect(() => {
    const timer = setTimeout(() => {
      if (printableItems.length > 0 && containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const firstItemWidthMM = printableItems[0]?.barcodeWidth || 50;
        // 1mm ≈ 3.78px at 96dpi
        const itemWidthPx = firstItemWidthMM * 3.78;

        // Floor division: how many full tags fit without stretching
        const cols = Math.max(1, Math.floor(containerWidth / itemWidthPx));
        setGridColumns(cols);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [printableItems]);

  const handleClose = () => setOpen(false);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    print: async (printIframe) => {
      const document = printIframe.contentDocument;
      if (document) {
        const style = document.createElement('style');
        style.textContent = `
          @page { margin: 0mm; size: auto; }
          body { margin: 0mm; padding: 0mm; }
        `;
        document.head.appendChild(style);
      }

      const result = await printIframe.contentWindow.print();
      return result;
    },
    onAfterPrint: () => console.log("Print completed"),
  });

  // Get first item's dimensions for grid calculation
  const firstItemWidthMM = printableItems[0]?.barcodeWidth || 50;
  const firstItemHeightMM = printableItems[0]?.barcodeHeight || 30;

  // Determine QR size relative to tag width
  const getQRSizeMM = (widthMM) => {
    if (widthMM <= 30) return 10;
    if (widthMM <= 40) return 12;
    if (widthMM <= 50) return 15;
    return 18;
  };

  return (
    <Index.Box className="qr-container">
      {/* Header */}
      <Index.Box className="qr-modal-header modal-header">
        <Index.Typography varient="h4" component="h4" className="qr-modal-title">
          Print Barcode
        </Index.Typography>

        <Index.Button className="modal-close-btn" onClick={handleClose}>
          <img
            src={Index.Png.Cancel}
            className="cancel-img"
            alt="close"
          />
        </Index.Button>
      </Index.Box>

      {/* Content */}
      <Index.Box className="qr-modal-content modal-content-main modal-scroll">
        <div ref={containerRef} style={{ width: "100%", minWidth: "300px" }}>

          {loading && (
            <div className="qr-empty-state">Generating Barcodes...</div>
          )}

          {!loading && dataError && (
            <div className="qr-empty-state">
              Error: {dataError}
            </div>
          )}

          {!loading && !dataError && printableItems.length === 0 && (
            <div className="qr-empty-state">
              No barcode data found. Please check the record data.
            </div>
          )}

          {/* 
            KEY FIX: Use `repeat(N, Xmm)` instead of `repeat(N, 1fr)`
            This ensures each card stays exactly at its physical mm width
            and doesn't stretch to fill the row.
          */}
          <Index.Box
            className="qr-card-main-box cus-card-main-box-barcode print-container"
            ref={printRef}
            style={{
              display: printableItems.length > 0 ? "grid" : "none",
              gridTemplateColumns: `repeat(${gridColumns}, ${firstItemWidthMM}mm)`,
            }}
          >
            {printableItems.map((item, index) => {
              const widthMM = parseFloat(item?.barcodeWidth) || 40;
              const heightMM = parseFloat(item?.barcodeHeight) || 40;

              return (
                <div
                  key={index}
                  // className="qr-barcode-card barcode-card"
                  className={`qr-barcode-card barcode-card ${parseFloat(heightMM) < 41 ? "barcode-height" : ""
                    }`}
                  style={{
                    width: `${widthMM}mm`,
                    height: `${heightMM}mm`,
                    "--col-width": `${widthMM}mm`,
                    "margin-right": '1mm',
                    "margin-left": '1mm'
                  }}
                >
                  {/* Brand Header */}
                  {(companyBrandImage || companyBrandName || companyAddress) && (
                    <div className="qr-header-section" style={fontSize ? { fontSize: `${fontSize}px` } : {}}>
                      {companyBrandImage ? (
                        <img
                          src={companyBrandImage}
                          alt="brand"
                          style={{ maxHeight: "18px", maxWidth: "100%", objectFit: "contain" }}
                        />
                      ) : companyBrandName ? (
                        <span className="qr-brand-name">{companyBrandName}</span>
                      ) : null}
                      {/* {companyAddress && (
                        <div style={{ fontSize: "7px", lineHeight: 1.2, marginTop: "1px" }}>
                          {companyAddress}
                        </div>
                      )} */}
                    </div>
                  )}

                  {/* Product Details */}
                  <div className={`qr-details-section ${!item?.barcodeScan ? "details-section-remove" : ""}`}
                    style={fontSize ? { fontSize: `${fontSize}px` } : {}}
                  >
                    {printDesignNo && (
                      <div className="qr-detail-row">
                        <span className="label-key" >D.NO.</span>
                        <span className="label-value">: {item?.productName}</span>
                      </div>
                    )}
                    {printColour && (
                      <div className="qr-detail-row">
                        <span className="label-key" >COL</span>
                        <span className="label-value">: {item?.color_name || item?.colorName}</span>
                      </div>
                    )}
                    {printSizeCM && (
                      <div className="qr-detail-row">
                        <span className="label-key" >SIZE</span>
                        <span className="label-value">: {item?.size_name || item?.sizeName}</span>
                      </div>
                    )}
                    {printStyle && (
                      <div className="qr-detail-row">
                        <span className="label-key" >STYLE</span>
                        <span className="label-value">: {item?.productGroupname || ""}</span>
                      </div>
                    )}
                    {printMFGDate && (
                      <div className="qr-detail-row">
                        <span className="label-key" >MFG DT.</span>
                        <span className="label-value">: {item?.createdAt
                          ? new Date(item.createdAt).toLocaleDateString("en-GB", {
                            month: "2-digit",
                            year: "numeric",
                          })
                          : ""}
                        </span>
                      </div>
                    )}
                    {printMRP && (
                      <div className="qr-detail-row">
                        <span className="label-key" >MRP</span>
                        <span className="label-value">: ₹{item?.mrp}/-</span>
                      </div>
                    )}
                    {printMRP && (
                      <div className="qr-detail-row" style={{ justifyContent: 'start' }}>
                        <span className="label-taxes">(Incl. of all taxes)</span>
                        {barcodeQty && <span className="label-taxes">QTY: {barcodeQty}</span>}
                      </div>
                    )}
                  </div>

                  {/* QR Code Footer */}
                  <div className={companyAddress ? "barcode-address-qr-div" : ""}>
                    <div className="barcode-address-div">
                      {companyAddress && (
                        <div style={{ fontSize: "8px", lineHeight: 1.2, maxWidth: "70px" }}>
                          {companyAddress}
                        </div>
                      )}
                    </div>
                    {(item?.barcodeScan !== 0 || item?.barcodeScan !== false || item?.barcodeScan !== 'false') && (
                      <div className="qr-footer-section footer-section-qr">
                        {item?.qrUrl ? (
                          <img
                            src={item.qrUrl}
                            alt="QR"
                            className="qr-image"
                            style={{
                              width: `${qrSizeMM}mm`,
                              height: `${qrSizeMM}mm`,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: `${qrSizeMM}mm`,
                              height: `${qrSizeMM}mm`,
                              // background: "#eee",
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </Index.Box>
        </div>
      </Index.Box>

      {/* Footer */}
      <Index.Box className="qr-modal-footer modal-footer-main">
        <Index.SecondaryButton
          className="btn-secondary base_cancel_btn"
          btnLabel="Cancel"
          onClick={handleClose}
        />
        <button className="btn-print" onClick={handlePrint} disabled={printableItems.length === 0}>Print</button>
      </Index.Box>
    </Index.Box>
  );
}